import {
  EnumFloorsType,
  JsonConciergeConditionDimension,
  JsonConciergeConfigCondition,
} from '@quality24/inpatient-typings';

/**
 * Input a `JsonConciergeConfigCondition` can be evaluated against. One field
 * per dimension the grammar currently supports.
 */
export interface ConciergeConditionInput {
  floorType: EnumFloorsType | null;
}

/** Dimension keys `matchesCondition` knows how to evaluate. */
const KNOWN_CONDITION_KEYS: ReadonlyArray<string> = ['always', 'floorType'];

const matchesDimension = <Value>(
  dimension: JsonConciergeConditionDimension<Value> | undefined,
  value: Value | null,
): boolean => {
  if (!dimension) return true;

  if (
    dimension.includes &&
    (value === null || !dimension.includes.includes(value))
  ) {
    return false;
  }

  if (
    dimension.excludes &&
    value !== null &&
    dimension.excludes.includes(value)
  ) {
    return false;
  }

  return true;
};

/**
 * Condition keys that this client's grammar does not recognize (e.g. a typo
 * like `floorTypes`). A condition with an unknown key should be treated as
 * unmatchable rather than as unrestricted — see `matchesCondition`, which
 * does not perform this check itself.
 */
export const getUnknownConditionKeys = (
  condition: Record<string, unknown> | null | undefined,
): string[] => {
  if (!condition) return [];
  return Object.keys(condition).filter(
    (key) => !KNOWN_CONDITION_KEYS.includes(key),
  );
};

/**
 * A condition counts as a hospital's unrestricted default only when it is
 * exactly empty or exactly `{ always: true }`. Anything else — including a
 * condition that only has unknown keys — is a specific (or malformed)
 * condition, never a default.
 */
export const isDefaultConciergeCondition = (
  condition: JsonConciergeConfigCondition | null | undefined,
): boolean => {
  if (!condition) return true;
  const keys = Object.keys(condition);
  if (keys.length === 0) return true;
  return keys.length === 1 && keys[0] === 'always' && condition.always === true;
};

/**
 * Evaluates a `JsonConciergeConfigCondition` against `input`. Same flattened
 * AND-of-dimensions grammar as `passRoomConditions`
 * (apps/inpatient-events/src/utils/roomEventUtils.ts): a dimension key that's
 * absent imposes no restriction; `includes` requires a non-null matching
 * value; `excludes` rejects a matching value (a null value is never excluded,
 * since it can't equal any listed value); `always` short-circuits everything
 * else to a match.
 *
 * Does not validate the condition's keys — callers that need to reject
 * unknown keys (e.g. typos) should check `getUnknownConditionKeys` first.
 */
export const matchesCondition = (
  condition: JsonConciergeConfigCondition | null | undefined,
  input: ConciergeConditionInput,
): boolean => {
  if (!condition) return true;
  if (condition.always) return true;

  return matchesDimension(condition.floorType, input.floorType);
};

interface ConciergeConfigWithCondition {
  id: string;
  condition?: JsonConciergeConfigCondition | null;
  createdAt?: string | null;
}

/**
 * Picks the applicable config among several rows of the same `type`, in
 * precedence order: (1) the most specific config — a non-empty condition,
 * with only known keys, that matches `input`; (2) the hospital's default —
 * an empty condition or `{ always: true }`; (3) none, when nothing above
 * qualifies (callers fall back to their own legacy behavior). A malformed
 * condition (unknown key) never qualifies for either level — it's dropped
 * with a console warning naming the config and the offending key(s), rather
 * than silently matching every floor.
 *
 * Ties within a level (no `createdAt`/id ordering exists in the data) are
 * broken deterministically by `createdAt` then `id`, with a console warning,
 * so the choice doesn't depend on server-returned order.
 */
export const selectConciergeConfig = <T extends ConciergeConfigWithCondition>(
  nodes: ReadonlyArray<T>,
  input: ConciergeConditionInput,
): T | undefined => {
  const sorted = [...nodes].sort((a, b) => {
    const createdAtCompare = (a.createdAt ?? '').localeCompare(
      b.createdAt ?? '',
    );
    return createdAtCompare !== 0 ? createdAtCompare : a.id.localeCompare(b.id);
  });

  const specific: T[] = [];
  const defaults: T[] = [];

  sorted.forEach((node) => {
    if (isDefaultConciergeCondition(node.condition)) {
      defaults.push(node);
      return;
    }

    const unknownKeys = getUnknownConditionKeys(node.condition);
    if (unknownKeys.length > 0) {
      // eslint-disable-next-line no-console
      console.warn(
        `[conciergeCondition] Config "${node.id}" has unrecognized condition ` +
          `key(s) (${unknownKeys.join(
            ', ',
          )}) and will never be selected — check for typos.`,
      );
      return;
    }

    if (matchesCondition(node.condition, input)) {
      specific.push(node);
    }
  });

  if (specific.length > 0) {
    if (specific.length > 1) {
      // eslint-disable-next-line no-console
      console.warn(
        `[conciergeCondition] Multiple configs match the same input ` +
          `(${specific.map((node) => node.id).join(', ')}) — using "${
            specific[0].id
          }".`,
      );
    }
    return specific[0];
  }

  if (defaults.length > 1) {
    // eslint-disable-next-line no-console
    console.warn(
      `[conciergeCondition] Multiple default configs found ` +
        `(${defaults.map((node) => node.id).join(', ')}) — using "${
          defaults[0].id
        }".`,
    );
  }

  return defaults[0];
};
